import { DefaultButton, Label, MessageBar, MessageBarType, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import { useCallback, useEffect, useState } from 'react';
import { HealAPI } from '../../../healApi';
import { ServiceOffering, SubService } from '../../../api/service-offerings-types';
import { css } from '../../../util/Css';
import { MultiSelector } from '../../core/MultiSelector';
import styles from '../components/VisitType.module.scss';
import { useCart } from '../CartContext';
import { useOktaAuth } from '@okta/okta-react';
import { isOpsManager } from '../../../okta';

interface Props {
  onServiceSelected: (subServiceCodeRequired: boolean) => void;
}

const SelectService = ({ onServiceSelected }: Props) => {
  const { state: cart, dispatch } = useCart();
  const [fetching, setFetching] = useState(false);
  const [services, setServices] = useState<ServiceOffering[]>([]);
  const [selectedService, setSelectedService] = useState<ServiceOffering>();
  const [selectedSubService, setSelectedSubService] = useState<SubService>();
  const [symptomsText, setSymptomsText] = useState<string>(cart.what.symptoms || '');
  const { authState } = useOktaAuth();
  const isManager: boolean = (authState?.accessToken && isOpsManager(authState.accessToken)) || false;

  useEffect(() => {
    let canceled = false;
    setFetching(true);
    if (cart.who.patientId && cart.where.addressId) {
      HealAPI.fetchServiceOfferings(cart.who.patientId, cart.where.addressId)
        .then((r) => r.data)
        .then((data) => {
          if (!canceled && data) {
            if (isManager) {
              setServices(data);
            } else {
              setServices(data.filter((s) => s.code !== 'MOBILE_SERVICES'));
            }
            if (cart.what.serviceCode) {
              const service = data.find((s) => s.code === cart.what.serviceCode);
              setSelectedService(service);
              if (service && cart.what.subServiceCode) {
                const subService = service.subServices.find((s) => s.code === cart.what.subServiceCode);
                setSelectedSubService(subService);
              }
            }
            setFetching(false);
          }
        });
    }
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.who.patientId, cart.where.addressId]);

  useEffect(() => {
    if (cart.what.symptoms !== symptomsText) {
      dispatch({ type: 'set_what', data: { ...cart.what, symptoms: symptomsText } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, symptomsText]);

  const onServiceSelect = useCallback(
    (service: ServiceOffering) => {
      setSelectedService(service);
      if (cart.what.serviceCode !== service.code || cart.what.symptoms !== symptomsText) {
        dispatch({ type: 'set_what', data: { serviceCode: service.code, subServiceCode: null, serviceName: service.shortName, symptomsRequired: service.symptomsRequired } });
        setSymptomsText('');
        setSelectedSubService(undefined);
        onServiceSelected(!!service.subServices);
      }
    },
    [dispatch, symptomsText, cart.what.serviceCode, cart.what.symptoms, onServiceSelected]
  );

  const onSubServiceSelect = useCallback(
    (subService: SubService) => {
      setSelectedSubService(subService);
      if (cart.what.subServiceCode !== subService.code) {
        dispatch({ type: 'set_what', data: { ...cart.what, subServiceCode: subService.code } });
      }
    },
    [dispatch, cart.what]
  );

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <Label className="ms-font-xl">Select Service</Label>
      {fetching && <Spinner style={{ marginTop: 20 }} size={SpinnerSize.large} label="Getting services..." />}
      {!fetching && services.length > 0 && (
        <>
          <Stack tokens={{ childrenGap: 16 }} horizontal>
            <div className={styles.listContainer} style={{ flexGrow: 1 }}>
              {services.map((reason: ServiceOffering) => (
                <ListItem key={reason.code} reason={reason} onSelect={onServiceSelect} selected={selectedService?.code === reason.code} testId={`list_${reason.code}`} />
              ))}
            </div>
            {!!selectedService?.subServices.length && (
              <div className={styles.listContainer} style={{ padding: 20, backgroundColor: '#f7fafc', flexGrow: 1 }}>
                {selectedService?.subServices?.map((sub: SubService) => (
                  <div key={sub.code}>
                    <DefaultButton
                      toggle
                      style={{
                        marginBottom: 10,
                        textAlign: 'left',
                        height: 45,
                        width: '100%',
                        backgroundColor: selectedSubService?.code === sub.code ? '#d4ecff' : 'white',
                        borderWidth: 2,
                        borderColor: selectedSubService?.code === sub.code ? 'rgb(184 210 237)' : '#edf2f7',
                        color: selectedSubService?.code === sub.code ? '#0078d4' : 'black',
                      }}
                      checked={selectedSubService?.code === sub.code}
                      text={sub.text}
                      onClick={() => onSubServiceSelect(sub)}
                    />
                  </div>
                ))}
              </div>
            )}
          </Stack>
          <SymptomsInput service={selectedService} symptomsText={symptomsText} setSymptomsText={setSymptomsText} />
        </>
      )}
      {!fetching && !services.length && (
        <MessageBar messageBarType={MessageBarType.error}>There are no services for this patient. This can be because of location or the patient.</MessageBar>
      )}
    </Stack>
  );
};

function SymptomsInput({ service, symptomsText, setSymptomsText }: { service?: ServiceOffering; symptomsText: string; setSymptomsText: (value: string) => void }) {
  if (service?.code === 'MOBILE_SERVICES') {
    // Here we are overloading the symtomps field to store the list of services to be performed.
    return (
      <MultiSelector
        hideAll={true}
        disabled={!service}
        required={true}
        label={'Services to be performed'}
        items={[
          // { key: 'Diabetic Retinal Eye Screening', text: 'Diabetic Retinal Eye Screening' },
          // { key: 'Peripheral Artery Disease Testing', text: 'Peripheral Artery Disease Testing' },
          { key: 'Lab Draw', text: 'Lab Draw' },
        ]}
        selectedKeys={!!symptomsText ? symptomsText.split(', ') : []}
        onChange={(keys) => setSymptomsText(keys.join(', '))}
      />
    );
  }

  return (
    <TextField
      label="Symptoms"
      disabled={!service}
      value={symptomsText}
      multiline={true}
      required={service?.symptomsRequired || false}
      rows={4}
      onChange={(ev, value) => setSymptomsText(value || '')}
      data-tid="inp_symptoms"
    />
  );
}

interface ListItemProps {
  onSelect: (reason: ServiceOffering) => void;
  reason: ServiceOffering;
  selected: boolean;
  testId: string;
}

const ListItem = (props: ListItemProps) => {
  const containerStyles = css(styles.listItem, {
    [styles.active]: props.selected,
  });

  const onClick = () => {
    props.onSelect(props.reason);
  };

  return (
    <table cellSpacing="0" className={containerStyles} onClick={onClick} data-tid={`service_${props.testId}`}>
      <tbody>
        <tr>
          <td className={styles.imageContainer}>
            <img src={props.reason.iconUrl} width="30" height="30" alt="" />
          </td>
          <td className={styles.textContainer}>
            <div className={styles.title}>
              <span style={{ fontWeight: 'bold' }}>{props.reason.shortName}</span>
            </div>
            <div className={styles.value}>{props.reason.serviceDescription}</div>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default SelectService;
